/**
 * WHAT THE AGENT OVERRODE, AND WHY — the override trail, on the review screen.
 *
 * WHY THIS EXISTS
 *   Every brief's pricing rationale carries the sentence "I can override any of these, and when I
 *   do, that decision is recorded as mine." `engine/field_override.py` is the record it promises,
 *   and `build_data()` puts it on the payload as `override_trail`. The three PDF layouts and the
 *   three Word twins print it under that same paragraph.
 *
 *   This is the seventh surface, and it is the only one that matters BEFORE the fact: the review
 *   screen is the last place an agent sees their own reason in the exact words a client will read
 *   it in, and the last place a badly worded one can be corrected. Showing the finished sentence
 *   here — not a summary of it, not a table standing in for it — is the whole point.
 *
 * IT RENDERS THE SERVER'S SENTENCE
 *   `entry.sentence` is composed once, in `field_override.sentence_of`, and every other surface
 *   prints that string. Composing a second wording here from `label` / `engine_value` /
 *   `agent_value` would make this component a fourth author of one recorded decision, which is the
 *   same failure `format_pairing.flag_of` exists to prevent for the outlier disclosure — an agent
 *   would approve one sentence and their client would receive another. ADR-0002: React is a pure
 *   renderer. The figures are shown as a header line because the payload already formatted both of
 *   them; nothing here computes, compares or rounds anything.
 *
 * SILENT WHEN THERE IS NOTHING TO SAY
 *   Nearly every brief overrides nothing, and this renders null for all of them. A heading over an
 *   empty list tells a reader the report has a section it does not have.
 */
import type { AmbReportData } from "./types";
import { themeVar as colors } from "../design-tokens";
import { HtmlSpan } from "./NarrativeReportScreen";

export type OverrideEntry = AmbReportData["override_trail"][number];

/** Exported for the test: reading the trail defensively is the part worth asserting.
 *
 * `override_trail` is unconditional on a payload built after the key landed, and ABSENT on one
 * built before it. Both mean the same thing to a renderer — no such judgement was recorded — so
 * they are collapsed here rather than distinguished in the markup. An entry with no `sentence` is
 * dropped rather than rendered as an empty row: the sentence IS the record. */
export function overrideEntries(data: AmbReportData): OverrideEntry[] {
  const raw = data?.override_trail;
  if (!Array.isArray(raw)) return [];
  return raw.filter((e) => !!e && typeof e.sentence === "string" && e.sentence.trim().length > 0);
}

export function OverrideTrail({ data }: { data: AmbReportData }) {
  const entries = overrideEntries(data);
  if (entries.length === 0) return null;

  return (
    <section style={{ marginTop: 20 }}>
      <h2 style={{ fontSize: 15 }}>What you overrode, and why</h2>
      <p style={{ fontSize: 13, color: colors.textSecondary, marginTop: 6, lineHeight: 1.55 }}>
        {/* THE TWO FACTS AN AGENT NEEDS BEFORE THEY SEND IT. That the figures in the brief did not
            move, so they are not looking for a changed number that is not there; and that this
            record cannot be edited out afterwards, so the wording below is the wording that
            ships. Both are properties of `field_override`, stated rather than implied. */}
        {entries.length === 1 ? "This override is" : `These ${entries.length} overrides are`} printed
        in the brief, word for word as below, under the pricing rationale. The figures the analysis
        reached are unchanged everywhere else in the document — your figure is recorded beside them,
        in your name. The trail is append-only: a decision already recorded cannot be removed, only
        corrected by recording the correction.
      </p>

      <div style={{ marginTop: 10 }}>
        {entries.map((e, i) => (
          <div
            key={`${e.field}-${e.at}-${i}`}
            style={{
              borderLeft: `3px solid ${colors.textPrimary}`,
              padding: "8px 0 8px 12px",
              marginTop: i === 0 ? 0 : 10,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>
              {e.label}
              {/* Both figures, as the payload formatted them. Monospaced for the same reason the
                  adjustment grid is: two money figures on one line are read by magnitude. */}
              <span style={{ fontWeight: 400, color: colors.textSecondary, marginLeft: 8 }}>
                <span style={{ fontFamily: "monospace" }}>{e.engine_value}</span>
                {" → "}
                <span style={{ fontFamily: "monospace" }}>{e.agent_value}</span>
              </span>
            </div>
            {/* THE SENTENCE CARRIES MARKUP, AND IT ALWAYS DID  (2026-09-01, W2-UIFIX).
                `types.ts` says so in terms of this very payload — "String fields here carry
                pre-formatted HTML fragments (e.g. `<b>`, `&ndash;`) by design ... render them as
                such" — and `field_override.sentence_of` bolds both figures inside the sentence so
                the reader can find them in three lines of prose. This line rendered `{e.sentence}`
                as React TEXT while every sibling field on the same payload went through `Html`, so
                the review screen printed, literally,
                `recorded as <b>$612,000</b> by Dana Whitfield` — on the one screen whose entire
                job is to show an agent the exact words a client will read.
                Escaping is not the safe direction here; it is a different wrong answer, and it is
                the more damaging one, because the deliverable was right and its on-screen twin was
                wrong. Same sink, same allow-list, same trust basis as the rest of the payload. */}
            <HtmlSpan
              html={e.sentence}
              style={{ display: "block", fontSize: 13, color: colors.textSecondary,
                       margin: "5px 0 0", lineHeight: 1.55 }}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

export default OverrideTrail;
